import { sellInvoiceStatusInversed } from '@teinor/erp/types/company/order/sell/sellInvoice'
import { SellCreateWorkSheetButton, SellDownloadButtonCSV, SellDownloadButtonPDF, SellDownloadPdfTemplateButton, SellDownloadTags, SellPdfPreview, SellViewWorkSheetButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { SellInvoiceDuplicateButton } from '../../../SellInvoiceButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { useMemo } from 'react'
import { getAllWorkSheetGroupStatuses } from '../../../../../../../../../../../utils/reducers/getters'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { OrderDivideIntoChaptersButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'

const SellInvoiceCreateNoPermissionButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellInvoiceCreateNoPermissionButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useSellInvoiceCreateNoPermissionButtonsLogic = ({ data, onSubmit, isReadOnly }: OrderStepButtonsBaseComponentProps) => {
    const {t} = useTwinTranslation()
    const name = 'sell_invoice_create_no_permission_step_buttons'
    const Order = data?.header
    const workSheets = getAllWorkSheetGroupStatuses()
    const showButtonByPricesPermission = !data?.header?.hidePrices

    const defaultButtons = [{ "id": "previewSellInvoice" }, { "id": "downloadSellInvoice" }, { "id": "duplicateSell" }, { "id": "createWorkSheet" }, { "id": "viewWorkSheet" }]

    const sellInvoiceDuplicateText = t('duplicateInvoice', 'Duplicar factura')
    const sellInvoiceDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const sellInvoiceDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellInvoicePDFPreviewText = t('previewPdf', 'Previsualizar PDF')
    const sellInvoiceDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellInvoiceDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const sellInvoiceViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellInvoiceCreateWorkSheetText = t('createWorkSheet', 'Crear hoja de trabajo')
    const sellInvoiceDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellInvoiceDuplicateButton,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellInvoiceDuplicateText,
                    id: 'duplicateSell',
                    allRowData: data,
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellInvoiceDownloadTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate: 'sellInvoice'
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellInvoiceDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate: 'sellInvoice'
                }
            },
            previewSellInvoice: {
                component: SellPdfPreview,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellInvoicePDFPreviewText,
                    id: 'previewSellInvoice',
                    Order,
                    typeTemplate: 'sellInvoice'
                }
            },
            downloadSellInvoiceTemplate: {
                component: SellDownloadPdfTemplateButton,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellInvoiceDownloadPDFTemplateText,
                    id: 'downloadSellInvoiceTemplate',
                    Order,
                    typeTemplate: 'sellInvoice',
                    key:'downloadSellInvoicePDFTemplate'
                }
            },
            downloadSellInvoice: {
                component: SellDownloadButtonPDF,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellInvoiceDownloadPDFText,
                    id: 'downloadSellInvoice',
                    Order,
                    typeTemplate: 'sellInvoice',
                    key:'downloadSellInvoice'
                }
            },
            viewWorkSheet: {
                component: SellViewWorkSheetButton,
                showButton: Order?.WorkSheetId && Order?.status === sellInvoiceStatusInversed['creating'],
                extraProps: {
                    title: sellInvoiceViewWorkSheetText,
                    id: 'viewWorkSheet',
                    WorkSheetId: Order?.WorkSheetId
                }
            },
            createWorkSheet: {
                component: SellCreateWorkSheetButton,
                showButton: !Order?.WorkSheetId && !!workSheets?.WorkSheetActiveStatusGroups?.length && Order?.status !== sellInvoiceStatusInversed['creating'],
                extraProps: {
                    title: sellInvoiceCreateWorkSheetText,
                    id: 'createWorkSheet',
                    Order,
                    createURL: '/api/app/order/sell/workSheet/createWorkSheetBySellInvoice',
                    orderField: 'SellInvoiceId',
                    completeData: data
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellInvoiceDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly,
                }
            },
        }
    }, [Order, data, onSubmit, sellInvoiceDivideIntoChaptersText, sellInvoiceDownloadCSVText, sellInvoiceDownloadPDFTemplateText, sellInvoiceDownloadPDFText, sellInvoiceDownloadTagsText, sellInvoiceDuplicateText, sellInvoicePDFPreviewText, sellInvoiceViewWorkSheetText, isReadOnly, sellInvoiceCreateWorkSheetText, workSheets?.WorkSheetActiveStatusGroups?.length, showButtonByPricesPermission ])

    return { name, ButtonComponents, defaultButtons }
}

export const SellInvoiceCreateNoPermissionButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellInvoiceCreateNoPermissionButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default SellInvoiceCreateNoPermissionButtons